// Querys and Mutations
import {
  GENERATE_PAYROLL,
  CREATE_PAYROLL,
  GET_WITH_PAGINATION,
  DELETE_PAYROLL,
  EXIST_PAYROLL,
  UPDATE_PAYROLL,
  EMPLOYEE_PAYROLL_LUNCH_DAYS,
  UPDATE_EMPLOYEE_PAYROLL_LUNCH_DAYS
} from '@/graphql/payroll/payroll'

// models
import {
  PayrollGenerated,
  PayrollInput,
  BiweeklyPayrollWithPagination,
  BiweeklyPayroll,
  PayrollLunchDay,
  PayrollLunchDaysResult
} from '@/models/payroll/PayrollTypes'

// apollo
import { apolloClient } from '@/apollo'

class PayrollService {
  /**
   * Queries
   */
  async generate (year: number, month: number, typeOfFortnight: number, work_category: number|null|string): Promise<PayrollGenerated[]> {
    const result = await apolloClient.query({
      query: GENERATE_PAYROLL,
      variables: {
        year: year,
        month: month,
        typeOfFortnight: typeOfFortnight,
        work_category: Number(work_category)
      }
    })
    return result.data.generatePayroll
  }

  async exist (year: number, month: number, typeOfFortnight: number, work_category: number|null|string): Promise<{
    exist: boolean;
    payroll: BiweeklyPayroll;
  }> {
    const result = await apolloClient.query({
      query: EXIST_PAYROLL,
      variables: {
        year: year,
        month: month,
        typeOfFortnight: typeOfFortnight,
        work_category: work_category
      }
    })
    return result.data.existPayroll
  }

  async getWithPagination (page: number, per_page: number): Promise<BiweeklyPayrollWithPagination> {
    const result = await apolloClient.query({
      query: GET_WITH_PAGINATION,
      variables: {
        page: page,
        first: per_page
      }
    })
    return {
      pagination: result.data.payrollWithPagination.paginatorInfo,
      objects: result.data.payrollWithPagination.data
    }
  }

  /**
   * Mutations
   */
  async create (form: PayrollInput): Promise<PayrollGenerated[]> {
    const result = await apolloClient.query({
      query: CREATE_PAYROLL,
      variables: {
        payroll: form
      }
    })
    return result.data.createPayroll
  }

  async update (id: number, form: PayrollInput): Promise<PayrollGenerated[]> {
    const result = await apolloClient.query({
      query: UPDATE_PAYROLL,
      variables: {
        payroll: form,
        id: id
      }
    })
    return result.data.updatePayroll
  }

  async delete (id: number): Promise<BiweeklyPayroll> {
    const result = await apolloClient.query({
      query: DELETE_PAYROLL,
      variables: {
        id: id
      }
    })
    return result.data.deletePayroll
  }

  async getLunchDays (employeeId: number|string, year: number, month: number, fortnight: number): Promise<PayrollLunchDay[]> {
    const result = await apolloClient.query({
      query: EMPLOYEE_PAYROLL_LUNCH_DAYS,
      variables: {
        employee_id: employeeId,
        year,
        month,
        fortnight
      }
    })
    return result.data.employeePayrollLunchDays
  }

  async updateLunchDays (input: {
    employee_id: number|string;
    year: number;
    month: number;
    fortnight: number;
    days: PayrollLunchDay[];
  }): Promise<PayrollLunchDaysResult> {
    const result = await apolloClient.mutate({
      mutation: UPDATE_EMPLOYEE_PAYROLL_LUNCH_DAYS,
      variables: {
        input: {
          employee_id: input.employee_id,
          year: input.year,
          month: input.month,
          fortnight: input.fortnight,
          days: input.days.map(day => ({
            date: day.date,
            source: day.source,
            item_id: day.item_id,
            amount_lunch: Number(day.amount_lunch) || 0,
            price_lunch: Number(day.price_lunch) || 0,
            has_record: !!day.has_record
          }))
        }
      }
    })
    return result.data.updateEmployeePayrollLunchDays
  }
}

export default new PayrollService()
